import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { api } from "../../lib/api";
import { fcfa } from "../../lib/format";

const PERIODS = [{ id: "day", label: "Aujourd'hui" }, { id: "week", label: "Cette semaine" }, { id: "month", label: "Ce mois" }];

function StatBox({ label, value, sub }) {
  return (
    <div className="bg-white border border-brand-line rounded-xl p-5">
      <p className="text-sm text-brand-muted">{label}</p>
      <p className="font-heading font-black text-2xl text-brand-ink mt-2">{value}</p>
      {sub && <p className="text-xs text-brand-muted mt-1">{sub}</p>}
    </div>
  );
}

export default function Reports() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get(`/admin/reports?period=${period}`).then(({ data }) => setData(data));
  }, [period]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Bénéfices & rapports</h1>
          <p className="text-brand-muted mt-1">Chiffre d'affaires, bénéfices et encaissements.</p>
        </div>
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`period-${p.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium ${period === p.id ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-sub hover:bg-brand-cream"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {data === null ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatBox label="Chiffre d'affaires" value={fcfa(data.total_ca)} sub={`${data.orders_count} vente(s)`} />
            <StatBox label="Bénéfice net" value={fcfa(data.total_profit)} />
            <StatBox label="Encaissé" value={fcfa(data.total_encaisse)} />
            <StatBox label="En attente" value={fcfa(data.total_en_attente)} />
          </div>

          <div className="bg-white border border-brand-line rounded-xl p-6">
            <h3 className="font-heading font-semibold text-brand-ink mb-4">Évolution CA & bénéfice</h3>
            {data.daily.length === 0 ? (
              <p className="text-sm text-brand-muted py-10 text-center">Aucune donnée sur cette période.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.daily} margin={{ left: -10 }}>
                  <defs>
                    <linearGradient id="ca" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#EAA021" stopOpacity={0.4} /><stop offset="95%" stopColor="#EAA021" stopOpacity={0} /></linearGradient>
                    <linearGradient id="pf" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2E7D32" stopOpacity={0.4} /><stop offset="95%" stopColor="#2E7D32" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DA" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#75867A" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#75867A" />
                  <Tooltip formatter={(v) => fcfa(v)} />
                  <Area type="monotone" dataKey="ca" name="CA" stroke="#EAA021" fill="url(#ca)" strokeWidth={2} />
                  <Area type="monotone" dataKey="profit" name="Bénéfice" stroke="#2E7D32" fill="url(#pf)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white border border-brand-line rounded-xl p-6">
            <h3 className="font-heading font-semibold text-brand-ink mb-4">Ventes par jour</h3>
            {data.daily.length === 0 ? (
              <p className="text-sm text-brand-muted py-10 text-center">Aucune donnée.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.daily} margin={{ left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DA" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#75867A" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#75867A" allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="orders" name="Ventes" fill="#839788" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
